import { ClipTranscriptSegment } from '@src/editor/models/ClipTranscriptSegment';
import { endsWithAbbreviation } from '@src/utility/strings';

import { fail, ok } from '../models/TranscriptResult';

import type { TranscriptResult } from '../models/TranscriptResult';
import type { ClipTranscriptToken } from '@src/editor/types';
import type { TranscriptElementLegacy } from '@src/types.ts/transcript';

// Phase 1: Pure punctuation helpers (no in-place mutation of caller segment)

export interface PunctuationChangeSummary {
  removedCount: number;
  restoredCount: number;
  fromIndex: number;
  toIndex: number;
}

/**
 * Remove punctuation tokens (type === 'punct') in [fromIndex, toIndex] producing a new segment.
 * Text tokens are preserved. Non-target punctuation untouched. Returns TranscriptResult.
 */
export function removePunctuationRange(
  segment: ClipTranscriptSegment,
  fromIndex: number,
  toIndex: number,
): TranscriptResult<{
  segment: ClipTranscriptSegment;
  summary: PunctuationChangeSummary;
}> {
  if (fromIndex > toIndex) return fail('invalid_range', { fromIndex, toIndex });
  const original = segment.getWordsWithPunctuation();
  const upperBound = original.length - 1;
  if (fromIndex < 0 || toIndex > upperBound) {
    return fail('index_out_of_bounds', {
      fromIndex,
      toIndex,
      upperBound,
      segmentId: segment.id,
    });
  }

  let changed = false;
  const nextTokens: ClipTranscriptToken[] = original.map((t, idx) => {
    if (idx < fromIndex || idx > toIndex) return t; // outside range
    if (t.type === 'punct' && t.state !== 'removed') {
      changed = true;
      return { ...t, state: 'removed' };
    }
    return t;
  });

  if (!changed) {
    return ok({
      segment,
      summary: { removedCount: 0, restoredCount: 0, fromIndex, toIndex },
    });
  }

  const removedCount = nextTokens.filter(
    (t, idx) =>
      idx >= fromIndex &&
      idx <= toIndex &&
      t.type === 'punct' &&
      t.state === 'removed',
  ).length;

  const newSegment = new ClipTranscriptSegment(
    nextTokens.filter(t => t.state !== 'removed'),
    segment.mediaStart,
    segment.mediaEnd,
  );
  return ok({
    segment: newSegment,
    summary: { removedCount, restoredCount: 0, fromIndex, toIndex },
  });
}

/**
 * Restore previously removed punctuation tokens (state === 'removed') in [fromIndex, toIndex].
 */
export function restorePunctuationRange(
  segment: ClipTranscriptSegment,
  fromIndex: number,
  toIndex: number,
): TranscriptResult<{
  segment: ClipTranscriptSegment;
  summary: PunctuationChangeSummary;
}> {
  if (fromIndex > toIndex) return fail('invalid_range', { fromIndex, toIndex });
  const original = segment.getWordsWithPunctuation();
  const upperBound = original.length - 1;
  if (fromIndex < 0 || toIndex > upperBound) {
    return fail('index_out_of_bounds', {
      fromIndex,
      toIndex,
      upperBound,
      segmentId: segment.id,
    });
  }
  let restoredCount = 0;
  const nextTokens: ClipTranscriptToken[] = original.map((t, idx) => {
    if (idx < fromIndex || idx > toIndex) return t;
    if (t.type === 'punct' && t.state === 'removed') {
      restoredCount += 1;
      const cloned: ClipTranscriptToken = { ...t } as ClipTranscriptToken;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (cloned as any).state;
      return cloned;
    }
    return t;
  });

  if (restoredCount === 0) {
    return ok({
      segment,
      summary: { removedCount: 0, restoredCount: 0, fromIndex, toIndex },
    });
  }

  const newSegment = new ClipTranscriptSegment(
    nextTokens,
    segment.mediaStart,
    segment.mediaEnd,
  );
  return ok({
    segment: newSegment,
    summary: { removedCount: 0, restoredCount, fromIndex, toIndex },
  });
}

export function trimTextAndFilterOutWhitespaces<
  T extends TranscriptElementLegacy,
>(tokens: readonly T[]): T[] {
  return tokens
    .filter(
      t =>
        t.type === 'text' ||
        (t.type === 'punct' && t.value && /[^\t ]/.test(t.value)),
    ) // new-lines allowed in punct
    .map(t => {
      if (t.type === 'text' && t.value) {
        t.value = t.value.trim();
      }
      return t;
    });
}

/**
 * Punctuation that typically doesn't require space before (English rules)
 */
const NO_SPACE_BEFORE = new Set([
  '.',
  ',',
  '!',
  '?',
  ';',
  ':',
  ')',
  ']',
  '}',
  '...',
  '—',
  '–',
  "'",
  '"',
]);

/**
 * Punctuation that typically doesn't require space after (English rules)
 */
const NO_SPACE_AFTER = new Set(['(', '[', '{', "'", '"', '\n']);

/**
 * Common website TLDs. Used to keep a domain like "example.com" intact by
 * suppressing the space (and the caption line break) we'd otherwise insert
 * after the period.
 */
const WEBSITE_TLDS = new Set([
  'com',
  'org',
  'net',
  'io',
  'co',
  'gov',
  'edu',
  'ai',
  'app',
]);

/**
 * True when a text value is a lowercase common website TLD (e.g. "com").
 * Lowercase-only so a genuine sentence boundary ("...done. Com...") is not
 * mistaken for a domain.
 */
function isWebsiteTld(value: string | null | undefined): boolean {
  return typeof value === 'string' && WEBSITE_TLDS.has(value);
}

/** A single lowercase domain label ("www", "example", "my-site"). */
const DOMAIN_LABEL = /^[a-z0-9-]+$/;

/** Minimal token shape the domain scan needs — text/punct plus a value. */
type DomainScanToken = { type?: string; value?: string | null };

/**
 * True when the token at `index` starts a domain tail: a run of
 * `label "." label …` that ends in a known TLD. The chain is what makes a
 * subdomain period work — in "www.example.com" the token after the first
 * period is "example", not a TLD, so a one-step check would treat that period
 * as a sentence end and split the domain (PLAT-10961).
 *
 * Multi-part TLDs ("example.co.uk") are NOT covered: nothing anchors "uk", so
 * the chain stops at "co" and the final period still reads as a sentence end.
 */
export function reachesWebsiteTld(
  tokens: readonly DomainScanToken[],
  index: number,
): boolean {
  for (let i = index; i < tokens.length; i += 2) {
    const label = tokens[i];
    if (
      label?.type !== 'text' ||
      !label.value ||
      !DOMAIN_LABEL.test(label.value)
    ) {
      return false;
    }
    if (isWebsiteTld(label.value)) return true;
    const period = tokens[i + 1];
    if (period?.type !== 'punct' || period.value !== '.') return false;
  }
  return false;
}

/**
 * How many tokens to look back when deciding whether a period closes an
 * acronym. AssemblyAI emits "U.S." both as one token ("U.S") and split into
 * single-letter tokens ("U" "." "S"), so four covers the longest split form.
 */
const ABBREVIATION_LOOKBACK = 4;

/**
 * True when a period sits inside a domain, e.g. either "." in
 * "www.example.com". Detected as: the token before `index` is a literal "."
 * and the tokens from `index` on form a domain tail ending in a known TLD.
 *
 * The backward look matters as much as the forward one: "net", "app", "ai",
 * "co" and "io" are ordinary words, so an acronym's period followed by one of
 * them ("the U.S. net revenue") would otherwise read as a domain and lose its
 * space. Reuses the shared acronym heuristic (PLAT-10961 / PLAT-10804).
 */
function isWebsitePeriod(
  tokens: readonly TranscriptElementLegacy[],
  index: number,
): boolean {
  const prevToken = tokens[index - 1];
  if (
    !prevToken ||
    prevToken.type !== 'punct' ||
    prevToken.value !== '.' ||
    !reachesWebsiteTld(tokens, index)
  ) {
    return false;
  }
  let preceding = '';
  for (let i = Math.max(0, index - 1 - ABBREVIATION_LOOKBACK); i < index; i++) {
    preceding += tokens[i].value ?? '';
  }
  return !endsWithAbbreviation(preceding);
}

/**
 * True for any whitespace-only punct token (real source whitespace OR a
 * previously-synthesized one). Mirrors the canonical whitespace predicate
 * used by every transcript renderer (`isWhitespaceElement` in
 * `components/transcript/utils.ts`) — keeping the rule value-based and
 * subtype-agnostic so it matches the actual data shape.
 */
function isWhitespaceToken(token: TranscriptElementLegacy): boolean {
  return (
    token.type === 'punct' &&
    typeof token.value === 'string' &&
    token.value.length > 0 &&
    token.value.trim() === ''
  );
}

/**
 * Determines if whitespace should be added before a token
 *
 * Exported because caption segmentation has to measure a row at the width the
 * renderer will draw it (`KaraokeClip.getKaraokeText` renders the row through
 * `fillInWhitespacesInTranscriptElements`, below) — the transcript track strips
 * every source whitespace token (`trimTextAndFilterOutWhitespaces`), so the
 * inter-word spaces exist only once this rule puts them back.
 */
export function needsSpaceBefore(
  tokens: readonly TranscriptElementLegacy[],
  index: number,
): boolean {
  const token = tokens[index];
  const prevToken = index > 0 ? tokens[index - 1] : undefined;
  if (!prevToken || !token.value) return false;

  // PLAT-9618: don't double up on whitespace. If the source already has a
  // whitespace token on either side of the boundary, the renderer will
  // emit that one — adding a synthetic on top sandwiched the real space
  // between two extras and produced "text,   text" (3 spaces) for the
  // common `text, comma, " ", text` pattern.
  if (isWhitespaceToken(token) || isWhitespaceToken(prevToken)) {
    return false;
  }

  // No space before punctuation that's in the NO_SPACE_BEFORE set
  if (token.type === 'punct' && NO_SPACE_BEFORE.has(token.value)) {
    return false;
  }

  // Add space between text tokens
  if (prevToken.type === 'text' && token.type === 'text') {
    return true;
  }

  // Keep a website domain intact: no space after the "." in "example.com"
  if (isWebsitePeriod(tokens, index)) {
    return false;
  }

  // Add space after punctuation (unless it's in NO_SPACE_AFTER)
  if (prevToken.type === 'punct' && prevToken.value) {
    return !NO_SPACE_AFTER.has(prevToken.value);
  }

  return false;
}

/**
 * Fill in whitespaces in transcript elements according to English language rules
 *
 * This function inserts space punct elements between tokens where appropriate,
 * following standard English typography rules:
 * - Space between words
 * - No space before: . , ! ? ; : ) ] } ' " etc.
 * - No space after: ( [ { ' " etc.
 * - Space after sentence-ending punctuation
 *
 * @param tokens - Array of transcript elements (text and punctuation)
 * @returns Array of transcript elements with space punct elements inserted
 *
 * @example
 * fillInWhitespacesInTranscriptElements([
 *   { type: 'text', value: 'Hello' },
 *   { type: 'punct', value: ',' },
 *   { type: 'text', value: 'world' },
 *   { type: 'punct', value: '!' },
 * ])
 * // Returns:
 * // [
 * //   { type: 'text', value: 'Hello' },
 * //   { type: 'punct', value: ',' },
 * //   { type: 'punct', value: ' ' },
 * //   { type: 'text', value: 'world' },
 * //   { type: 'punct', value: '!' },
 * // ]
 *
 * @example
 * fillInWhitespacesInTranscriptElements([
 *   { type: 'punct', value: '(' },
 *   { type: 'text', value: 'Note' },
 *   { type: 'punct', value: ')' },
 * ])
 * // Returns:
 * // [
 * //   { type: 'punct', value: '(' },
 * //   { type: 'text', value: 'Note' },
 * //   { type: 'punct', value: ')' },
 * // ]
 */
export function fillInWhitespacesInTranscriptElements<
  T extends TranscriptElementLegacy,
>(tokens: T[]): T[] {
  if (tokens.length === 0) return [];

  const result: T[] = [];

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    // Insert space before current token if needed
    if (needsSpaceBefore(tokens, i)) {
      const spaceToken = {
        type: 'punct',
        subtype: 'whitespace',
        state: token.state,
        value: ' ',
        initial_index: -1,
      } as T;
      result.push(spaceToken);
    }

    result.push(token);
  }

  return result;
}

export function addSpaceToEndOfTranscriptElements<
  T extends TranscriptElementLegacy,
>(tokens: T[]): T[] {
  if (tokens.length === 0) return [];

  const lastToken = tokens[tokens.length - 1];
  if (lastToken.subtype === 'whitespace' || lastToken.value === ' ') {
    return tokens; // already has space at the end
  }

  return [
    ...tokens,
    {
      type: 'punct',
      subtype: 'whitespace',
      value: ' ',
      initial_index: -1,
    } as T,
  ];
}
